import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Auth } from '@aws-amplify/auth';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import {
  SurveyReportResponse,
  SurveyReportQuestion,
  SurveyReportQuestionType,
  SurveyReportScaleAnswer,
  SurveyReportRankedAnswer,
  SurveyReportDistributionItem,
} from '../model/survey';

interface QuestionMeta {
  no: number;          // バックエンドの設問番号（回答データ・レポートデータの照合用。保存キーの番号）
  displayNo?: number;  // 画面に表示する番号（省略時は no と同じ）
  text: string;
  topFive?: boolean;
}

type ReportCell =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'ranked'; items: SurveyReportRankedAnswer[] }
  | { kind: 'distribution'; items: SurveyReportDistributionItem[] };

export interface ReportRow {
  no: number;
  displayNo: number;
  text: string;
  topFive: boolean;
  you: ReportCell;
  org: ReportCell;
  otherOrg: ReportCell;
}

@Component({
  selector: 'app-survey-report',
  templateUrl: './survey-report.component.html',
  styleUrl: './survey-report.component.css',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
  ]
})
export class SurveyReportComponent implements OnInit {
  public readonly Constant = Constant;
  public showSpinner = true;
  public report: SurveyReportResponse | null = null;
  public targetMonthLabel = '';
  public showOtherOrg = false;

  public selfRows: ReportRow[] = [];
  public workplaceRows: ReportRow[] = [];

  private targetMonth = '';
  private apiPath = '/survey/report/';

  private readonly selfQuestionMeta: QuestionMeta[] = [
    { no: 1, text: '今の仕事に前向きに取り組めている' },
    { no: 2, text: '自分の仕事には意味・やりがいがあると感じる' },
    { no: 3, text: '自分の仕事や活動には、自分で工夫・判断できる余地がある' },
    { no: 4, text: '最近、自分が成長している実感がある' },
    { no: 5, text: '自分の仕事や行動が、誰かや職場の役に立っている' },
  ];

  private readonly workplaceQuestionMeta: QuestionMeta[] = [
    { no: 6, text: '業務量・内容は適正である' },
    { no: 7, text: '公平性・納得感がある' },
    { no: 8, text: '安心して意見を言える' },
    { no: 9, text: '上司から必要な支援を得られている' },
    { no: 10, text: '周囲との協力・支援関係がある' },
    { no: 11, text: '努力や貢献が認識・承認されている' },
    { no: 12, text: '無理なく働き続けられる見通しがある' },
    { no: 13, text: '職場/店舗の課題', topFive: true },
    { no: 14, text: '課題の改善を妨げている主な要因', topFive: true },
    { no: 15, text: '職場の一番の課題の根拠', topFive: true },
    // 回答画面と同じく、旧16（組合として今後優先すべき関わり方）を旧17・18の後ろに置き、表示番号を振り直す
    { no: 17, displayNo: 16, text: 'この1か月で、担当職場に退職者は出ましたか' },
    { no: 18, displayNo: 17, text: 'この1か月で、担当職場に休職者は出ましたか' },
    { no: 16, displayNo: 18, text: 'この職場に対して、組合として今後優先すべき関わり方は何ですか', topFive: true },
    { no: 19, text: '組合員の組合活動への参加意欲' },
    { no: 20, text: '組合関連課題', topFive: true },
    { no: 21, text: '組合員の生活への期待企画', topFive: true },
  ];

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private router: Router,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private auth: AuthService,
  ) {
    this.title.setTitle(Constant.pageTitleSurveyReport + Constant.pageTitleCommon);
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    const rawMonth = this.route.snapshot.paramMap.get('targetMonth') || '';
    // URLは "202609" のようにハイフン無しの形式にしているため、API呼び出し用に "2026-09" へ戻す
    this.targetMonth = rawMonth.length === 6 ? `${rawMonth.slice(0, 4)}-${rawMonth.slice(4)}` : rawMonth;
    this.loadReport();
  }

  private loadReport(): void {
    this.showSpinner = true;

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);

      this.commonUtil.apiGet(this.apiPath + this.targetMonth, options)
        .then(res => {
          const report: SurveyReportResponse = res.data;
          this.report = report;
          this.targetMonthLabel = this.formatTargetMonth(report.targetMonth);
          // データの有無ではなく、発行時点の権限で確定したフラグをそのまま使う
          this.showOtherOrg = report.showOtherOrg;

          const isMember = report.viewType === 'member';
          this.selfRows = this.buildRows(this.selfQuestionMeta, report.questions, isMember);
          this.workplaceRows = this.buildRows(this.workplaceQuestionMeta, report.questions, isMember);

          this.showSpinner = false;
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          this.showSpinner = false;
          if (err.data?.notFound || err.data?.accessDenied) {
            this.router.navigate(['/error']);
          } else if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }
        });
    });
  }

  private formatTargetMonth(targetMonth: string): string {
    const [year, month] = targetMonth.split('-');
    return `${Number(year)}年 ${Number(month)}月分`;
  }

  onBack(): void {
    this.router.navigate(['/survey']);
  }

  private buildRows(metas: QuestionMeta[], questions: SurveyReportQuestion[], isMember: boolean): ReportRow[] {
    return metas.map(meta => {
      const q = questions.find(x => x.no === meta.no);
      if (!q) {
        return {
          no: meta.no, displayNo: meta.displayNo ?? meta.no, text: meta.text, topFive: !!meta.topFive,
          you: { kind: 'empty' }, org: { kind: 'empty' }, otherOrg: { kind: 'empty' },
        };
      }
      return {
        no: meta.no,
        displayNo: meta.displayNo ?? meta.no,
        text: meta.text,
        topFive: !!meta.topFive,
        you: this.buildCell(q.you, q.type, isMember),
        org: this.buildCell(q.org, q.type, false),
        otherOrg: this.buildCell(q.otherOrg, q.type, false),
      };
    });
  }

  // バックエンドから返る値（設問種別と、本人の回答か集計値かで形が異なる）を
  // テンプレート側で扱いやすい表示用の形（ReportCell）に正規化する。
  private buildCell(value: unknown, type: SurveyReportQuestionType, isOwn: boolean): ReportCell {
    // 集計対象が0件の場合、Top5等は空配列（nullではない）で返ってくるため、
    // こちらもブランクではなく「-」表示になるようemptyとして扱う
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      return { kind: 'empty' };
    }
    switch (type) {
      case 'scale': {
        if (isOwn) {
          const s = value as SurveyReportScaleAnswer;
          return { kind: 'text', text: [s.value, s.label].filter(v => v != null).join(' ') };
        }
        return { kind: 'text', text: (value as number).toFixed(1) };
      }
      case 'ranked': {
        if (isOwn) {
          return { kind: 'ranked', items: value as SurveyReportRankedAnswer[] };
        }
        return { kind: 'list', items: value as string[] };
      }
      case 'multi': {
        return { kind: 'list', items: value as string[] };
      }
      case 'single': {
        if (isOwn) {
          return { kind: 'text', text: value as string };
        }
        return { kind: 'list', items: value as string[] };
      }
      case 'distribution': {
        if (isOwn) {
          return { kind: 'text', text: value as string };
        }
        return { kind: 'distribution', items: value as SurveyReportDistributionItem[] };
      }
      default:
        return { kind: 'empty' };
    }
  }
}
